import { useEffect, useState, useCallback } from "react";

const STORAGE_KEY = "aim-admin-view-all";

/**
 * Pure URL-building helper: appends `all=true` (respecting an existing `?`)
 * when `viewAll` is true; returns `url` unchanged otherwise.
 */
export function buildScopedUrl(url: string, viewAll: boolean): string {
  if (!viewAll) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}all=true`;
}

/** Same-tab broadcast: every mounted `useAdminScope` follows a toggle. */
const SCOPE_EVENT = "aim-admin-scope";

function writeStoredScope(value: boolean): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, value ? "1" : "0");
  window.dispatchEvent(new CustomEvent<boolean>(SCOPE_EVENT, { detail: value }));
}

/**
 * Consume a landing `?all=true` / `?all=false` ONCE, at module load — the
 * same "a link can carry scope" idea `wallet/shell/flag.ts` uses for
 * `?shell=`, so a dashboard tile or a shared URL can put an admin straight
 * into family-wide mode instead of the previous silent no-op (2026-08-28
 * admin-console audit: typing `?all=true` in the address bar did nothing,
 * because this toggle was localStorage-only).
 *
 * UNLIKE `flag.ts`, the param is deliberately NOT stripped from the URL
 * afterwards. `all=` is a second, independent contract here: `?all=true` is
 * ALSO read directly off `searchParams` as a page-local, per-request scope
 * override by `lib/dashboard/drilldown.ts`-built links, `RecordsPage` and
 * `useAnalyticsParams` (V5-34) — nothing to do with this localStorage toggle,
 * and those readers need the param still sitting in the URL the moment they
 * mount. Stripping it here would silently break every dashboard drill-through
 * link. So this only SEEDS the persisted toggle; the query string is left
 * exactly where the linking page put it.
 */
function consumeScopeQueryParam(): void {
  if (typeof window === "undefined" || !window.location?.search) return;
  const value = new URLSearchParams(window.location.search).get("all");
  if (value === "true") writeStoredScope(true);
  else if (value === "false") writeStoredScope(false);
}

consumeScopeQueryParam();

/**
 * Admin-only "all family data" toggle. Persists in localStorage under
 * `aim-admin-view-all` (distinct from the rent module's own
 * `aim-rent-admin-view-all` key — the two toggles are independent).
 *
 * `withScope(url)` appends `?all=true` (or `&all=true`) to a fetch URL when
 * the toggle is ON; callers should include `viewAll` in their TanStack Query
 * `queryKey` so toggling triggers a refetch.
 */
export function useAdminScope() {
  const [viewAll, setViewAllState] = useState(false);

  // Hydrate from localStorage on mount (SSR-safe: starts false, syncs on
  // client). `consumeScopeQueryParam()` above has already run by this point
  // (module evaluation happens before any component's effects), so a landing
  // `?all=` has already been folded into localStorage here.
  useEffect(() => {
    if (typeof window === "undefined") return;
    setViewAllState(window.localStorage.getItem(STORAGE_KEY) === "1");
    // Each screen holds its own copy of the flag; without this a toggle in the
    // rail updated only the rail, and a page refetched with its stale scope.
    const onScope = (e: Event) => setViewAllState(Boolean((e as CustomEvent<boolean>).detail));
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setViewAllState(e.newValue === "1");
    };
    window.addEventListener(SCOPE_EVENT, onScope);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(SCOPE_EVENT, onScope);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const setViewAll = useCallback((v: boolean) => {
    setViewAllState(v);
    writeStoredScope(v);
  }, []);

  const withScope = useCallback(
    (url: string) => buildScopedUrl(url, viewAll),
    [viewAll],
  );

  return { viewAll, setViewAll, withScope };
}
